/**
 * Location Performance — report data.
 *
 * Like the other reports, nothing is fetched until `run()` is called with a
 * validated filter set: the query groups over every conversation in the window
 * and an unbounded default would be an expensive query nobody asked for.
 */
import { useCallback, useState } from 'react'
import { api } from '../api/client.js'

export interface LocationPerformanceRow {
  groupKey: string
  groupLabel: string
  conversations: number
  sharePct: number | null
  open: number
  resolved: number
  closed: number
  unanswered: number
  avgResponseSeconds: number | null
  avgResolutionSeconds: number | null
  slaMeasured: number
  slaBreached: number
  slaBreachPct: number | null
  olaMeasured: number
  olaBreached: number
  olaBreachPct: number | null
  csatCount: number
  csatAvg: number | null
}

export interface LocationPerformanceFilters {
  timezone: string
  groupBy: string
  startDate: string
  endDate: string
  locationIds: string[]
  branchIds: string[]
  departmentIds: string[]
}

export function buildLocationPerformanceParams(
  filters: LocationPerformanceFilters,
): URLSearchParams {
  const params = new URLSearchParams({
    timezone: filters.timezone,
    groupBy: filters.groupBy,
    startDate: filters.startDate,
    endDate: filters.endDate,
  })
  if (filters.locationIds.length > 0) params.set('locationIds', filters.locationIds.join(','))
  if (filters.branchIds.length > 0) params.set('branchIds', filters.branchIds.join(','))
  if (filters.departmentIds.length > 0) {
    params.set('departmentIds', filters.departmentIds.join(','))
  }
  return params
}

interface UseReportResult {
  rows: LocationPerformanceRow[]
  /** Header for the grouped column — follows the chosen dimension. */
  groupLabel: string
  loading: boolean
  error: string | null
  hasRun: boolean
  run: (filters: LocationPerformanceFilters) => Promise<void>
  reset: () => void
}

export function useLocationPerformanceReport(): UseReportResult {
  const [rows, setRows] = useState<LocationPerformanceRow[]>([])
  const [groupLabel, setGroupLabel] = useState('Location')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasRun, setHasRun] = useState(false)

  const run = useCallback(async (filters: LocationPerformanceFilters): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{
        rows: LocationPerformanceRow[]
        meta: { groupLabel?: string }
      }>(`/reports/conversations/location-performance?${buildLocationPerformanceParams(filters)}`)
      setRows(res.rows ?? [])
      setGroupLabel(res.meta?.groupLabel ?? 'Location')
      setHasRun(true)
    } catch (err) {
      setRows([])
      setHasRun(true)
      setError(err instanceof Error ? err.message : 'Could not load the report.')
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback((): void => {
    setRows([])
    setHasRun(false)
    setError(null)
  }, [])

  return { rows, groupLabel, loading, error, hasRun, run, reset }
}
